import { useCallback, useEffect, useRef, useState, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeft, ChevronRight, GripHorizontal, Maximize2, Minimize2, PictureInPicture2, X } from 'lucide-react'
import { cn } from '../../lib/shadcn/utils'

type Stored = {
  collapsed: boolean
  floating: boolean
  full: boolean
  top: number
  left: number
}

function readStored(id: string): Partial<Stored> {
  try {
    const raw = localStorage.getItem(`fleetly.panel.${id}`)
    return raw ? (JSON.parse(raw) as Partial<Stored>) : {}
  } catch {
    return {}
  }
}

function writeStored(id: string, state: Stored) {
  try {
    localStorage.setItem(`fleetly.panel.${id}`, JSON.stringify(state))
  } catch {
    // Storage can be unavailable (private browsing, quota). Not worth surfacing.
  }
}

function centeredPosition(width: number, height: number) {
  const vw = window.innerWidth
  const vh = window.innerHeight
  return {
    left: Math.max(16, Math.round((vw - width) / 2)),
    top: Math.max(16, Math.round((vh - height) / 2)),
  }
}

// A pane that can collapse to a narrow rail, or pop out into a floating,
// draggable window that can be centred or expanded to fill the screen. Used
// at lg+ so the statement pane (and its neighbours) can always be read in
// full even on a narrow desktop window. Below lg the caller renders its own
// plain mobile layout instead of using this component at all.
export function Panel({
  id,
  title,
  widthClass,
  floatWidth = 720,
  floatHeight = 640,
  children,
}: {
  // Unique key for persisting this panel's layout choice.
  id: string
  title: string
  // Tailwind width classes applied when the panel sits inline in the row,
  // e.g. "w-80 shrink-0" or "flex-1 min-w-0".
  widthClass: string
  floatWidth?: number
  floatHeight?: number
  children: ReactNode
}) {
  const [collapsed, setCollapsed] = useState(() => readStored(id).collapsed ?? false)
  const [floating, setFloating] = useState(() => readStored(id).floating ?? false)
  const [full, setFull] = useState(() => readStored(id).full ?? false)
  const [pos, setPos] = useState(() => {
    const saved = readStored(id)
    if (saved.top != null && saved.left != null) return { top: saved.top, left: saved.left }
    return centeredPosition(floatWidth, floatHeight)
  })

  useEffect(() => {
    writeStored(id, { collapsed, floating, full, top: pos.top, left: pos.left })
  }, [id, collapsed, floating, full, pos])

  const dragState = useRef<{ startX: number; startY: number; top: number; left: number } | null>(null)

  const onDragPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (full) return
      dragState.current = { startX: e.clientX, startY: e.clientY, top: pos.top, left: pos.left }
      e.currentTarget.setPointerCapture(e.pointerId)
    },
    [full, pos],
  )

  const onDragPointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragState.current) return
    const dx = e.clientX - dragState.current.startX
    const dy = e.clientY - dragState.current.startY
    setPos({
      top: Math.max(0, dragState.current.top + dy),
      left: Math.max(0, dragState.current.left + dx),
    })
  }, [])

  const onDragPointerUp = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    dragState.current = null
    e.currentTarget.releasePointerCapture(e.pointerId)
  }, [])

  const handleCentre = useCallback(() => {
    setFull(false)
    setPos(centeredPosition(floatWidth, floatHeight))
  }, [floatWidth, floatHeight])

  const handlePopOut = useCallback(() => {
    setPos(centeredPosition(floatWidth, floatHeight))
    setFull(false)
    setFloating(true)
    setCollapsed(false)
  }, [floatWidth, floatHeight])

  const handleDock = useCallback(() => setFloating(false), [])

  // Collapsed to a narrow rail: no content, just a title and an expand button.
  if (collapsed && !floating) {
    return (
      <div className="hidden shrink-0 flex-col items-center gap-2 border-r border-border bg-muted/30 py-3 lg:flex lg:w-9">
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          title={`Expand ${title}`}
          aria-label={`Expand ${title}`}
          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <span
          className="mt-1 whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          {title}
        </span>
      </div>
    )
  }

  const header = (
    <div className="flex h-8 shrink-0 items-center gap-1 border-b border-border bg-muted/40 px-2">
      {floating && (
        <div
          onPointerDown={onDragPointerDown}
          onPointerMove={onDragPointerMove}
          onPointerUp={onDragPointerUp}
          title="Drag to move"
          className="flex cursor-grab items-center rounded p-1 text-muted-foreground active:cursor-grabbing"
        >
          <GripHorizontal className="h-3.5 w-3.5" />
        </div>
      )}
      <span className="flex-1 truncate text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </span>
      {floating && (
        <>
          <button
            type="button"
            onClick={handleCentre}
            className="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <PictureInPicture2 className="h-3.5 w-3.5" />
            Centre
          </button>
          <button
            type="button"
            onClick={() => setFull((f) => !f)}
            className="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            {full ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
            Full width
          </button>
          <button
            type="button"
            onClick={handleDock}
            title="Dock panel"
            aria-label="Dock panel"
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </>
      )}
      {!floating && (
        <>
          <button
            type="button"
            onClick={handlePopOut}
            title="Pop out and move"
            aria-label="Pop out and move"
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <PictureInPicture2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            title={`Collapse ${title}`}
            aria-label={`Collapse ${title}`}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
        </>
      )}
    </div>
  )

  if (floating) {
    const style = full
      ? { top: 16, left: 16, right: 16, bottom: 16, width: 'auto', height: 'auto' }
      : { top: pos.top, left: pos.left, width: floatWidth, height: floatHeight }
    return (
      <>
        {/* Docked placeholder so the row keeps its usual shape while this pane is floating. */}
        <div className="hidden shrink-0 flex-col items-center gap-2 border-r border-border bg-muted/30 py-3 lg:flex lg:w-9">
          <button
            type="button"
            onClick={handleDock}
            title={`Dock ${title}`}
            aria-label={`Dock ${title}`}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span
            className="mt-1 whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            {title}
          </span>
        </div>
        {createPortal(
          <div
            className="fixed z-40 hidden flex-col overflow-hidden rounded-lg border border-border bg-card shadow-retool-lg lg:flex"
            style={style}
          >
            {header}
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
          </div>,
          document.body,
        )}
      </>
    )
  }

  return (
    <div className={cn('hidden min-h-0 flex-col border-r border-border lg:flex', widthClass)}>
      {header}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
    </div>
  )
}
